import { useState } from "react";
import { Upload, Star } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { http, errMsg, fileUrl } from "@/lib/api";
import { F, inputCls, StatusPill, ConfirmDelete, MoveButtons, moveItem, PublishControls } from "@/components/admin/shared";

const CONSENT = { pending: "Izin belum dikonfirmasi", approved: "Izin publikasi disetujui", declined: "Izin ditolak", not_required: "Tidak ada wajah teridentifikasi" };

const UploadForm = ({ categories, reload }) => {
  const [f, setF] = useState({ file: null, category_id: categories[0]?.id || "", caption: "", consent: "pending" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.file) return toast.error("Pilih foto terlebih dahulu.");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await http.post("/admin/photos", fd);
      toast.success("Foto diunggah sebagai draft");
      setF({ ...f, file: null, caption: "" });
      e.target.reset();
      reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-2xl bg-white border border-nanina-line p-4 mb-5" data-testid="photo-upload-form">
      <F label="Foto (JPG/PNG/WEBP, maks 8 MB)"><input type="file" accept="image/jpeg,image/png,image/webp" className={inputCls} onChange={(e) => setF({ ...f, file: e.target.files[0] })} data-testid="photo-file-input" /></F>
      <F label="Kategori"><select className={inputCls} value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="photo-category-select">{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></F>
      <F label="Caption"><input className={inputCls} value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="photo-caption-input" /></F>
      <F label="Status izin publikasi"><select className={inputCls} value={f.consent} onChange={(e) => setF({ ...f, consent: e.target.value })} data-testid="photo-consent-select">{Object.entries(CONSENT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></F>
      <button disabled={busy} className="sm:col-span-2 lg:col-span-4 inline-flex justify-center items-center gap-2 rounded-full bg-nanina-green text-white text-sm font-semibold py-2.5 disabled:opacity-60" data-testid="photo-upload-button"><Upload className="h-4 w-4" /> {busy ? "Mengunggah..." : "Unggah foto"}</button>
    </form>
  );
};

const PhotoCard = ({ p, categories, onUpdate, onRemove, onMove, reload }) => {
  const [caption, setCaption] = useState(p.caption);
  const canPublish = ["approved", "not_required"].includes(p.consent);
  return (
    <div className="rounded-2xl bg-white border border-nanina-line overflow-hidden" data-testid={`admin-photo-${p.id}`}>
      <div className="relative aspect-[4/3] bg-nanina-bg">
        <img src={fileUrl(p.id)} alt={p.caption || "Foto"} className="h-full w-full object-cover" />
        <span className="absolute top-2 left-2"><StatusPill item={p} /></span>
        {p.featured && <Star className="absolute top-2 right-2 h-5 w-5 fill-nanina-butter text-nanina-butter" />}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex gap-2">
          <input className={`${inputCls} !mt-0`} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption" data-testid={`photo-caption-edit-${p.id}`} />
          <button onClick={() => onUpdate({ caption })} className="rounded-xl bg-nanina-freshsoft text-nanina-green text-xs font-semibold px-3" data-testid={`photo-caption-save-${p.id}`}>Simpan</button>
        </div>
        <select className={`${inputCls} !mt-0`} value={p.category_id} onChange={(e) => onUpdate({ category_id: e.target.value })} data-testid={`photo-category-edit-${p.id}`}>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select className={`${inputCls} !mt-0`} value={p.consent} onChange={(e) => onUpdate({ consent: e.target.value })} data-testid={`photo-consent-edit-${p.id}`}>{Object.entries(CONSENT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <div className="flex items-center justify-between text-xs text-nanina-green">
          {canPublish ? <PublishControls item={p} coll="photos" reload={reload} testId={`photo-${p.id}`} /> : <span className="text-[11px] text-nanina-muted">Belum bisa terbit</span>}
          <label className="flex items-center gap-2">Unggulan <Switch checked={p.featured} onCheckedChange={(v) => onUpdate({ featured: v })} data-testid={`photo-featured-switch-${p.id}`} /></label>
        </div>
        {!canPublish && <p className="text-[11px] text-[#8A6D3B]">Konfirmasi izin publikasi sebelum menerbitkan.</p>}
        <div className="flex justify-between">
          <MoveButtons onUp={() => onMove(-1)} onDown={() => onMove(1)} testId={`photo-move-${p.id}`} />
          <ConfirmDelete label="foto ini" onConfirm={onRemove} testId={`photo-delete-${p.id}`} />
        </div>
      </div>
    </div>
  );
};

export const PhotosPanel = ({ categories, photos, reload }) => {
  const [filter, setFilter] = useState("all");
  const list = photos.filter((p) => filter === "all" || p.category_id === filter);
  const update = async (p, data) => {
    try { await http.put(`/admin/photos/${p.id}`, data); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => { await http.delete(`/admin/photos/${p.id}`); toast.success("Foto dihapus"); reload(); };
  const move = async (i, d) => { if (await moveItem(list, i, d, "/admin/photos/reorder")) reload(); };
  return (
    <div data-testid="photos-panel">
      <UploadForm categories={categories} reload={reload} />
      <select className={`${inputCls} !mt-0 max-w-xs mb-4`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="photos-category-filter">
        <option value="all">Semua kategori</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {list.length === 0 && <p className="rounded-2xl border border-dashed border-nanina-line p-6 text-center text-sm text-nanina-muted">Belum ada foto.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((p, i) => (
          <PhotoCard key={p.id} p={p} categories={categories} reload={reload} onUpdate={(d) => update(p, d)} onRemove={() => remove(p)} onMove={(d) => move(i, d)} />
        ))}
      </div>
    </div>
  );
};
